import { useEffect, useRef, useState, type ReactNode } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Calendar } from "@/components/shadcn/calendar";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { CalendarIcon, Clock, X } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { cn } from "@/lib/utils";
import { NoxusFormLabel } from "./NoxusFormLabel";

export type DatePickerValueFormat = "datetime" | "date";

interface DatePickerInputProps {
  value: string;
  onChange: (value: string) => void;
  valueFormat?: DatePickerValueFormat;
  /** Shows an inline clear button on the trigger when a date is set. */
  onClear?: () => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  includeTime?: boolean;
  isRequired?: boolean;
  /** Open the calendar immediately (e.g. when used as an inline cell editor). */
  defaultOpen?: boolean;
  /** Notified when the popover opens/closes. */
  onOpenChange?: (open: boolean) => void;
  /** Custom popover trigger. Replaces the default outline button — e.g. a
   *  seamless cell that keeps showing its value. Must be a single element. */
  renderTrigger?: ReactNode;
  /** Pick and show the time in UTC instead of the browser's time zone. */
  utc?: boolean;
}

interface DatePickerFieldProps {
  name: string;
  label: string;
  tooltip?: string;
  control: Control<any>;
  isDisabled?: boolean;
  defaultValue?: string;
  includeTime?: boolean;
  placeholder?: string;
  rules?: RegisterOptions;
}

const parseValue = (value: string): Date | undefined => {
  if (!value) return undefined;
  const date = parseISO(value);
  return isValid(date) ? date : undefined;
};

const formatDateForDisplay = (
  value: string,
  includeTime: boolean,
  utc: boolean
): string => {
  const date = parseValue(value);
  if (!date) return "";
  const timeZone = utc ? "UTC" : undefined;

  try {
    if (includeTime) {
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone,
      });
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone,
    });
  } catch {
    return "";
  }
};

const formatTimeForInput = (date: Date, utc: boolean): string => {
  const hours = String(utc ? date.getUTCHours() : date.getHours());
  const minutes = String(utc ? date.getUTCMinutes() : date.getMinutes());
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

// The calendar works in local days, so a UTC value is shown on its UTC day.
const calendarDay = (date: Date, utc: boolean): Date =>
  utc
    ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    : date;

const atTime = (day: Date, hours: number, minutes: number, utc: boolean) => {
  const parts = [day.getFullYear(), day.getMonth(), day.getDate()] as const;
  return utc
    ? new Date(Date.UTC(...parts, hours, minutes))
    : new Date(...parts, hours, minutes);
};

// Up to four digits filling HH then MM. A leading digit that cannot start
// an hour (3-9) or a minute (6-9) is zero-padded, so "930" reads as 09:30.
const toTimeDigits = (raw: string): string => {
  let digits = "";
  for (const d of raw.replace(/\D/g, "")) {
    if (digits.length === 0 && d > "2") digits += "0";
    if (digits.length === 2 && d > "5") digits += "0";
    digits += d;
  }
  return digits.slice(0, 4);
};

const formatTimeDigits = (digits: string): string =>
  digits ? `${digits.slice(0, 2)}:${digits.slice(2)}` : "";

const parseTimeDigits = (digits: string): string | null => {
  if (!digits) return null;
  const hours = digits.slice(0, 2).padStart(2, "0");
  const minutes = digits.slice(2).padEnd(2, "0");
  if (Number(hours) > 23 || Number(minutes) > 59) return null;
  return `${hours}:${minutes}`;
};

// Attached only to the selected option, so React calls it when the columns
// open and whenever the selection moves to another option.
const centerInColumn = (option: HTMLElement | null) => {
  const column = option?.parentElement;
  if (!option || !column) return;
  column.scrollTop =
    option.offsetTop - column.clientHeight / 2 + option.clientHeight / 2;
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

export const DatePickerInput = ({
  value,
  onChange,
  valueFormat = "datetime",
  onClear,
  onBlur,
  isDisabled = false,
  placeholder,
  label,
  tooltip,
  includeTime = false,
  isRequired,
  defaultOpen = false,
  onOpenChange,
  renderTrigger,
  utc = false,
}: DatePickerInputProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const showTime = includeTime && valueFormat === "datetime";
  const [timeValue, setTimeValue] = useState<string>(() => {
    const date = showTime ? parseValue(value) : undefined;
    return date ? formatTimeForInput(date, utc) : "00:00";
  });

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onChange("");
      return;
    }

    if (valueFormat === "date") {
      onChange(format(selectedDate, "yyyy-MM-dd"));
      setIsOpen(false);
      return;
    }

    const [hours, minutes] = showTime
      ? timeValue.split(":").map(Number)
      : [0, 0];
    onChange(atTime(selectedDate, hours || 0, minutes || 0, utc).toISOString());

    if (!showTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (newTime: string) => {
    setTimeValue(newTime);

    const date = parseValue(value);
    if (date) {
      const [hours, minutes] = newTime.split(":").map(Number);
      const day = calendarDay(date, utc);
      onChange(atTime(day, hours || 0, minutes || 0, utc).toISOString());
    }
  };

  const parsed = parseValue(value);
  const selectedDate = parsed && calendarDay(parsed, utc);

  // Custom time popup styled like the calendar (the native <input type="time">
  // dropdown can't be themed). A box + clock icon opens two value columns.
  const [timeOpen, setTimeOpen] = useState(false);
  const timeWrapRef = useRef<HTMLDivElement>(null);
  const { refs: timeRefs, floatingStyles: timeFloatingStyles } = useFloating({
    open: timeOpen,
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [
      offset(({ placement }) =>
        placement.startsWith("right") || placement.startsWith("left") ? 20 : 4
      ),
      flip({
        crossAxis: false,
        fallbackPlacements: ["right-end", "left-end", "top-start"],
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const [hh, mm] = timeValue.split(":");
  const [timeDraft, setTimeDraft] = useState<string | null>(null);

  const handleTimeDraftChange = (raw: string) => {
    const previous = timeDraft ?? timeValue;
    let digits = toTimeDigits(raw);
    // Backspacing over the fixed colon removes the hour digit before it.
    if (raw.length < previous.length && digits === toTimeDigits(previous)) {
      digits = digits.slice(0, -1);
    }
    setTimeDraft(formatTimeDigits(digits));
    const parsed = parseTimeDigits(digits);
    if (parsed) handleTimeChange(parsed);
  };

  const commitTimeDraft = () => {
    if (timeDraft === null) return;
    const parsed = parseTimeDigits(toTimeDigits(timeDraft));
    if (parsed) handleTimeChange(parsed);
    setTimeDraft(null);
  };

  useEffect(() => {
    if (!timeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!timeWrapRef.current?.contains(e.target as Node)) setTimeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [timeOpen]);

  return (
    <div className="space-y-2">
      {label && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          isRequired={isRequired}
        />
      )}
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          onOpenChange?.(open);
        }}
      >
        {renderTrigger ? (
          <PopoverTrigger asChild>{renderTrigger}</PopoverTrigger>
        ) : (
          <div className="relative">
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={isDisabled}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground",
                  onClear && "pr-8"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {value
                  ? formatDateForDisplay(value, showTime, utc)
                  : placeholder || "Pick a date"}
              </Button>
            </PopoverTrigger>
            {onClear && value && !isDisabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className={cn(
                  "absolute right-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded",
                  "text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                )}
                aria-label="Clear date"
                title="Clear date"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={handleDateSelect}
            disabled={isDisabled}
            initialFocus
          />
          {showTime && (
            <div className="border-t p-3">
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm font-medium">Time:</span>
                <div ref={timeWrapRef} className="relative">
                  <div ref={timeRefs.setReference} className="relative w-32">
                    <Input
                      aria-label="Time"
                      inputMode="numeric"
                      placeholder="HH:MM"
                      disabled={isDisabled}
                      value={timeDraft ?? timeValue}
                      onFocus={(e) => {
                        e.currentTarget.select();
                        setTimeOpen(true);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeOpen(true);
                      }}
                      onChange={(e) => handleTimeDraftChange(e.target.value)}
                      onBlur={commitTimeDraft}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitTimeDraft();
                          setTimeOpen(false);
                        }
                      }}
                      className="pr-8"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      disabled={isDisabled}
                      aria-label="Toggle time options"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeOpen((o) => !o);
                      }}
                      className="absolute right-0 top-0 flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground disabled:cursor-not-allowed"
                    >
                      <Clock className="size-3.5" />
                    </button>
                  </div>
                  {timeOpen && (
                    <div
                      ref={timeRefs.setFloating}
                      style={timeFloatingStyles}
                      className="z-50 flex w-40 gap-2 rounded-md border bg-popover p-2 shadow-md"
                    >
                      <div
                        role="listbox"
                        aria-label="Hours"
                        className="scrollbar-none flex max-h-[200px] flex-1 flex-col gap-1 overflow-y-auto"
                      >
                        {HOURS.map((h) => (
                          <button
                            key={h}
                            type="button"
                            role="option"
                            aria-selected={h === hh}
                            ref={h === hh ? centerInColumn : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTimeChange(`${h}:${mm}`);
                            }}
                            className={cn(
                              "shrink-0 cursor-pointer rounded-md py-1.5 text-center text-sm transition-colors",
                              h === hh
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            {h}
                          </button>
                        ))}
                      </div>
                      <div
                        role="listbox"
                        aria-label="Minutes"
                        className="scrollbar-none flex max-h-[200px] flex-1 flex-col gap-1 overflow-y-auto"
                      >
                        {MINUTES.map((m) => (
                          <button
                            key={m}
                            type="button"
                            role="option"
                            aria-selected={m === mm}
                            ref={m === mm ? centerInColumn : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTimeChange(`${hh}:${m}`);
                            }}
                            className={cn(
                              "shrink-0 cursor-pointer rounded-md py-1.5 text-center text-sm transition-colors",
                              m === mm
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const DatePickerField = ({
  name,
  label,
  tooltip,
  control,
  isDisabled,
  defaultValue,
  includeTime = false,
  placeholder,
  rules,
}: DatePickerFieldProps) => {
  return (
    <Controller
      control={control}
      disabled={isDisabled}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }) => {
        return (
          <DatePickerInput
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            isDisabled={isDisabled}
            label={label}
            tooltip={tooltip}
            includeTime={includeTime}
            placeholder={placeholder}
            isRequired={!!rules?.required}
          />
        );
      }}
      name={name}
    />
  );
};
